import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen, Loader2, ShieldAlert, Scale, Shield, LifeBuoy } from "lucide-react";
import Layout from "@/components/Layout";
import api from "@/lib/api";

const ICONS = {
  "non-harassment": ShieldAlert,
  "non-discrimination": Scale,
  "anti-retaliation": Shield,
  "bringing-a-concern": LifeBuoy,
};

export default function Resources() {
  const [policies, setPolicies] = useState(null);

  useEffect(() => {
    api.get("/policies").then((r) => setPolicies(r.data)).catch(() => setPolicies([]));
  }, []);

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <span className="text-xs font-bold tracking-[0.2em] uppercase text-terracotta-500">Resources</span>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-forest-900 leading-tight max-w-2xl">Policies & guidance to help you feel informed.</h1>
        <p className="mt-4 text-charcoal-600 max-w-xl">Understand your protections and how the ER CoE applies Patria's policies consistently across campuses.</p>

        {policies === null ? (
          <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-forest-600" /></div>
        ) : (
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {policies.map((p, i) => {
              const Icon = ICONS[p.slug] || BookOpen;
              return (
                <motion.div key={p.slug} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.06 }}>
                  <Link
                    to={`/resources/${p.slug}`}
                    data-testid={`resource-card-${p.slug}`}
                    className="group block h-full bg-white rounded-2xl border border-forest-600/10 p-8 shadow-[0_4px_24px_rgba(44,74,59,0.04)] hover:-translate-y-1 hover:shadow-lg transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <span className="grid place-items-center h-12 w-12 rounded-xl bg-forest-50 text-forest-600 group-hover:bg-terracotta-50 group-hover:text-terracotta-500 transition-colors">
                        <Icon className="h-6 w-6" strokeWidth={1.5} />
                      </span>
                      <span className="text-[11px] uppercase tracking-wide text-charcoal-600 bg-bone-100 rounded-full px-3 py-1">{p.category}</span>
                    </div>
                    <h2 className="mt-5 font-serif text-2xl text-forest-900">{p.title}</h2>
                    <p className="mt-2 text-charcoal-600 leading-relaxed">{p.summary}</p>
                    <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-forest-700 group-hover:gap-2 transition-all">
                      Read more <ArrowRight className="h-4 w-4" strokeWidth={2} />
                    </span>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}
      </section>
    </Layout>
  );
}
